import { useActiveContext } from "shared/ContextSwitcher/store";
import { useState } from "react";
import * as Menu from "src/components/ui-components/MenuPanel/MenuPanel";
import { AnchorButton } from "src/components/ui-components/Button/AnchorButton";
import cn from "classnames";
import styles from "./DatasetSettings.module.css";
import { ControlledClickPopover } from "src/components/ui-components/ClickPopover";
import { IconButton } from "src/components/ui-components/Button/IconButton";
import { IconThreeDots } from "src/components/ui-components/Icons/IconThreeDots";
import { ModalWithoutTrigger } from "src/components/ui-components/Modal";
import { DeleteDatasetModalContent } from "./modals/DeleteDatasetModalContent";
import { ChangeVisibilityModalContent } from "./modals/ChangeVisibilityModalContent";
import { canOwnPrivateDataset } from "shared/DataUpload/utils";

export const DatasetSettings = ({
  isPrivate,
  tableName,
  createdAt,
}: {
  createdAt: string;
  isPrivate: boolean;
  tableName: string;
}) => {
  const activeContext = useActiveContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteDatasetModalOpened, setIsDeleteDatasetModalOpened] =
    useState(false);
  const [isChangeVisibilityModalOpened, setIsChangeVisibilityModalOpened] =
    useState(false);

  // This is a solution to prevent editing of deprecated datasets.
  // Deprecated datasets are datasets that were created before 2023-10-03.
  const canUpdateSettings =
    !tableName.startsWith("dune_upload.") &&
    new Date(createdAt) > new Date("2023-10-03T00:00:00.000Z");

  const canMakeDatasetPrivate = canOwnPrivateDataset({
    serviceTierName: activeContext?.serviceTier.name,
    isCustomPlan: activeContext?.serviceTier.hasCustomPlan,
  });

  const visibilityChangeDisabled = !isPrivate && !canMakeDatasetPrivate;
  const onVisibilityChange = () => {
    if (visibilityChangeDisabled) {
      return;
    }

    setIsChangeVisibilityModalOpened(true);
    setIsOpen(false);
  };

  const onDeleteDataset = () => {
    setIsDeleteDatasetModalOpened(true);
    setIsOpen(false);
  };

  const menu = () => (
    <Menu.Panel>
      <Menu.Section title="">
        <Menu.ItemButton
          onClick={onVisibilityChange}
          nestedButtonsOrLinks={
            visibilityChangeDisabled && (
              <AnchorButton theme="secondary-light" size="XS" href="/pricing">
                Upgrade plan
              </AnchorButton>
            )
          }
        >
          <span className={cn(visibilityChangeDisabled && styles.disabled)}>
            Make dataset {isPrivate ? "public" : "private"}
          </span>
        </Menu.ItemButton>
        <Menu.ItemButton onClick={onDeleteDataset}>
          <span className={styles.linkSecondary}>Delete dataset</span>
        </Menu.ItemButton>
      </Menu.Section>
    </Menu.Panel>
  );

  return (
    <td className={cn(styles.settings, isOpen && styles.opened)}>
      {canUpdateSettings && (
        <ControlledClickPopover
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          position="below-align-right"
          content={menu}
        >
          <IconButton theme="ghost" size="S">
            <IconThreeDots />
          </IconButton>
        </ControlledClickPopover>
      )}

      <ModalWithoutTrigger
        size="M"
        label="Delete dataset"
        content={
          <DeleteDatasetModalContent
            tableName={tableName}
            onDismiss={() => setIsDeleteDatasetModalOpened(false)}
          />
        }
        isOpen={isDeleteDatasetModalOpened}
        onDismiss={() => setIsDeleteDatasetModalOpened(false)}
      />

      <ModalWithoutTrigger
        size="M"
        label="Change visibility"
        content={
          <ChangeVisibilityModalContent
            isPrivate={isPrivate}
            tableName={tableName}
            onDismiss={() => setIsChangeVisibilityModalOpened(false)}
          />
        }
        isOpen={isChangeVisibilityModalOpened}
        onDismiss={() => setIsChangeVisibilityModalOpened(false)}
      />
    </td>
  );
};
